// Plays the short notification chimes (message / mention). Mirrors the call
// `RingtoneManager` but non-looping and lower volume. Lazily constructs one
// HTMLAudioElement per sound and reuses it; autoplay rejections are swallowed
// (a chime is a nicety, not a correctness invariant).

import type { NotificationSound } from '@ping/ui';

type SoundKey = NotificationSound | 'typing';

const SOURCES: Record<SoundKey, string> = {
  message: '/sounds/message.wav',
  mention: '/sounds/mention.wav',
  typing: '/sounds/typing.mp3',
};

class MessageSoundManager {
  private cache = new Map<SoundKey, HTMLAudioElement>();
  private lastPlayedAt = 0;
  private lastTypingAt = 0;

  private playKey(sound: SoundKey, volume: number): void {
    if (typeof Audio === 'undefined') return;
    let audio = this.cache.get(sound);
    if (!audio) {
      audio = new Audio(SOURCES[sound]);
      audio.volume = volume;
      this.cache.set(sound, audio);
    }
    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p !== undefined) p.catch(() => undefined);
    } catch {
      /* best-effort */
    }
  }

  play(sound: NotificationSound): void {
    // Throttle: a burst of messages shouldn't machine-gun the speaker.
    const now = Date.now();
    if (now - this.lastPlayedAt < 800) return;
    this.lastPlayedAt = now;
    this.playKey(sound, 0.45);
  }

  /** Soft chime when the peer starts typing in the active chat. Throttled
   *  more aggressively than messages so rapid type/pause cycles don't spam. */
  playTyping(): void {
    const now = Date.now();
    if (now - this.lastTypingAt < 4000) return;
    this.lastTypingAt = now;
    this.playKey('typing', 0.3);
  }
}

export const messageSoundManager = new MessageSoundManager();
